"use client"

import React, { useState } from "react"
import { cn } from "@/lib/utils"
import { InputRowChildProps } from "./types"

interface BaseInputRowProps<C = React.ReactNode> {
  /**
   * Optional additional class names to style the row container.
   */
  className?: string

  /**
   * This could be either a simple React.ReactNode (which is what it defaults to) when using the
   * simple styled InputRow, or if you're using InputRowWithUpdates, this will be a more complex
   * group of child components that will have access to value and setValue render props.
   */
  children: C
}

interface UpdatingInputRowProps<T>
  extends BaseInputRowProps<
    ({ value, setValue }: InputRowChildProps<T>) => React.ReactNode
  > {
  /**
   * The initial text to manage in this row's shared state.
   */
  initialValue: T
}

/**
 * A container component that maintains a shared state among its children using render props.
 *
 * @example You'll need to use this with render props, so the structure will look like this:
 *          <InputRowWithUpdates initialValue={[INSERT_HERE]}>
 *            {({ value, setValue }) => (
 *              <>
 *                <InputDescription>[INSERT_HERE]</InputDescription>
 *                <InputSubtitle>[INSERT_HERE]</InputSubtitle>
 *                ...
 *              </>
 *            )}
 *          </InputRowWithUpdates>
 */
export function InputRowWithUpdates<T>({
  initialValue,
  className,
  children,
}: UpdatingInputRowProps<T>) {
  // Local state for the value this is a controller for.
  const [value, setValue] = useState<T>(initialValue)

  // We use this as a wrapper around the useState's Dispatch so that we're not leaking it through the abstraction layer.
  // That way the using function can just pass in a generic setValue without having a direct dependency on Dispatch.
  const setValueWrapper = (val: T) => {
    setValue(val)
  }

  return (
    <InputRow className={className}>
      {children({ value, setValue: setValueWrapper })}
    </InputRow>
  )
}

/**
 * A simple InputRow with just the styling, and none of the auto update render props for managing state.
 */
export function InputRow({ className, children }: BaseInputRowProps) {
  return (
    <div className={cn("flex flex-col space-y-2 w-full my-4", className)}>
      {children}
    </div>
  )
}
